// © 2001-2015 INOVx Solutions, Inc.
// 114 Pacifica Suite 300, Irvine, CA 92618 USA
// All rights reserved.
//
// This software is confidential, proprietary and unpublished property of
// INOVx Solutions, Inc.  It may NOT be copied in part or in whole on any
// medium, either electronic or printed, without the express written consent
// of INOVx Solutions, Inc.

import * as BABYLON from 'babylonjs';
import * as Interfaces from '../../Interfaces/Interfaces';
import * as Enumerations from '../../Enumerations/Enumerations';
import * as Data from '../../viewers/Data/WebData';
import * as WorkerGeometry from './WorkerGeometry';

class MeshRepository implements Interfaces.Interfaces.IMeshRepository {

    private MERGE_MESH_THRESHOLD = 200;
    private MESH_VERTES_SPLIT_THRESHOLD = 100000;
    private _babScene: BABYLON.Scene;
    private _sceneContentExtents: BABYLON.BoundingBox;

    // Map of meshes and their indexes to object id.
    private _masterMeshToObjectInfo: { [masterMeshId: string]: SubMeshObjectInfo[]; } = {};
    private _mergeMeshesExtentInfo: { [masterMeshId: string]: { [objectId: string]: BABYLON.BoundingInfo; }; } = {};
    private _masterMeshKeys: string[] = [];
    private _remappedMeshId: { [objectId: string]: number; } = {};
    private _masterMeshes: { [masterMeshId: string]: any; } = {};

    private _mergedMeshKeys: string[] = [];

    private _meshIdentifier: string | number;
    // When meshManager is told to add a mesh, meshes are added here, until they are later merged into "mega" meshes (the mergedMeshes collection). When this merging occurs, they are removed from this
    // collection, and mered into the second collection, mergedMeshes.
    private _meshesToMerge: { [appearanceId: string]: WorkerGeometry.GeometryData[]; } = {};

    private _mergeMeshVertex: { [appearanceId: string]: number; } = {};
    private _instancePayload: Data.GraphicData.AssetInstancePayload[] = [];
    public numMerges: number = 0;
    public maxMerge: number = 0;

    // Helper array used to keep track of what materials have been added to the primitives. This is useful because we are organizing meshesToMerge, and mergedMeshes based on materials as the key.

    get meshExtents(): BABYLON.BoundingBox {
        return this._sceneContentExtents;
    }

    constructor(scene: BABYLON.Scene, meshIdentifier: string | number) {
        this._babScene = scene;
        this._meshIdentifier = meshIdentifier;
        this._masterMeshKeys = [];
    }

    dispose() {
        this._masterMeshKeys = [];
        this._masterMeshToObjectInfo = {};
        this._mergeMeshesExtentInfo = {};
        this._remappedMeshId = {};
        this._instancePayload = [];

        this._mergedMeshKeys = [];
        for (var k in this._masterMeshes) {
            this.disposeStoredGeometry(this._masterMeshes[k]);
            delete this._masterMeshes[k];
        }
        this._masterMeshes = {};
        this._instancePayload = [];
    }
    getMeshData(meshes: BABYLON.Mesh[]) {
        var retVal: any[] = [];

        for (var i = 0; i < meshes.length; i++) {

            var mesh = meshes[i];

            // There is no information of indicies of the meshes to their assets, so we say the object info
            // is the entire mesh, index 0-length, using the mesh id as the object info iteslf.

            var uvBuffer = mesh.getVertexBuffer(BABYLON.VertexBuffer.UVKind);
            var uvs = uvBuffer ? meshes[i].getVertexBuffer(BABYLON.VertexBuffer.UVKind).getData() : null;
            var insertUvIndex = uvs ? meshes[i].getVertexBuffer(BABYLON.VertexBuffer.UVKind).getSize() : 0;

            var objectInfo = new SubMeshObjectInfo();

                objectInfo.objectId = meshes[i].id;
                objectInfo.indiciesStartIndex = 0;
                objectInfo.vertexStartIndex = 0;
                objectInfo.uvStartIndex = 0;
                objectInfo.indicesCount = meshes[i].getTotalIndices();
                objectInfo.vertexCount = meshes[i].getTotalVertices();
                objectInfo.uvCount = insertUvIndex;
                objectInfo.minExtents = new BABYLON.Vector3(0,0,0);
                objectInfo.maxExtents = new BABYLON.Vector3(0,0,0);
                objectInfo.materialOverride = "";

            if (mesh.geometry == null) {
                
            }
            else {
                retVal.push({
                    Key: mesh.name,
                    Geometry: this.serializeGeometry(mesh.geometry),
                    ObjectInfo: [objectInfo],
                    IdToMaster: [mesh.id],
                    MaterialKey: (mesh.material != null) ? mesh.material.id : null
                });
            }
        }
            
        return retVal;
    }
    getRepositoryMeshData(): any {
        try {
            var retVal: any[] = [];
            var keys = Object.keys(this._masterMeshes);

            if (this._masterMeshKeys != null) {
                for (var i = 0; i < this._masterMeshKeys.length; i++) {
                    if (this._masterMeshKeys[i] != null && this._masterMeshes[this._masterMeshKeys[i]] != null) {
                        var objectInfo = this._masterMeshToObjectInfo[this._masterMeshKeys[i]];

                        if (objectInfo != null) {
                            var meshIds: any[] = [];
                            for (var x = 0; x < objectInfo.length; x++) {
                                meshIds.push(objectInfo[x].objectId);
                            }

                            var materialKey = this._masterMeshKeys[i].split("_")[0];

                            var serialGeo = this.serializeStoredGeometry(this._masterMeshes[this._masterMeshKeys[i]]);

                            retVal.push({
                                Key: this._masterMeshKeys[i],
                                Geometry: serialGeo,
                                ObjectInfo: objectInfo,
                                IdToMaster: meshIds,
                                MaterialKey: materialKey
                            });
                        }
                        else {
                            console.log("Empty info! " + this._masterMeshKeys[i]);
                        }
                    }
                    else {
                        console.log("Skipped");
                    }
                }
            }
            else {
                console.log("KEYS NULL!");
            }

            return retVal;
        }
        catch (error) {
            console.log(error);
            return [];
        }
    }

    serializeGeometry(geometry: BABYLON.Geometry) {
        // Positions / normals / indices / uvs as before
        const positions = new Float32Array(geometry.getVerticesData(BABYLON.VertexBuffer.PositionKind) || []);
        const normals = new Float32Array(geometry.getVerticesData(BABYLON.VertexBuffer.NormalKind) || []);
        const indices = WorkerGeometry.Utils.cloneIndices(geometry.getIndices());
        const uvs = new Float32Array(geometry.getVerticesData(BABYLON.VertexBuffer.UVKind) || []);

        // --- Colors: preserve the original typed array + attach metadata ---
        let colors: Float32Array | Uint8Array | null = null;
        let colorInfo: { componentType: "FLOAT32" | "UNSIGNED_BYTE"; normalized: boolean; components: number } | null = null;

        const vbColor = geometry.getVertexBuffer(BABYLON.VertexBuffer.ColorKind);
        if (vbColor) {
            // vbColor.getData() returns an ArrayBufferView (typed array) when possible.
            const data = vbColor.getData() as ArrayBufferView | null;

            // Component size (should be 4 for RGBA in your pipeline)
            const comps = vbColor.getSize();

            // Component type
            const isU8 = vbColor.type === BABYLON.VertexBuffer.UNSIGNED_BYTE;
            const isF32 = vbColor.type === BABYLON.VertexBuffer.FLOAT || vbColor.type === undefined;
            // (Some Babylon builds leave type undefined for default float buffers.)

            if (data && data.buffer) {
                // IMPORTANT: Make a typed view on the *same underlying buffer* so it can be transferred.
                if (isU8) {
                    const src = data as Uint8Array;
                    colors = new Uint8Array(src.buffer, src.byteOffset, src.byteLength);
                    colorInfo = { componentType: "UNSIGNED_BYTE", normalized: !!vbColor.normalized, components: comps };
                } else {
                    // Treat anything else as float32
                    const src = data as Float32Array;
                    colors = new Float32Array(src.buffer, src.byteOffset, src.byteLength / Float32Array.BYTES_PER_ELEMENT);
                    colorInfo = { componentType: "FLOAT32", normalized: false, components: comps };
                }
            } else {
                // Fallback: getVerticesData returns numbers/Float32 copy
                const f = new Float32Array(geometry.getVerticesData(BABYLON.VertexBuffer.ColorKind) || []);
                colors = f;
                colorInfo = { componentType: "FLOAT32", normalized: false, components: comps || 4 };
            }
        } else {
            // No color buffer
            colors = new Float32Array(); // empty
            colorInfo = { componentType: "FLOAT32", normalized: false, components: 4 };
        }

        return {
            id: geometry.id,
            positions,
            normals,
            indices,
            uvs,
            colors,        // <-- can be Float32Array OR Uint8Array now
            colorInfo      // <-- tell the main thread how to rebuild the VB
        };
    }

    private serializeStoredGeometry(storedGeometry: any) {
        if (storedGeometry == null) {
            return null;
        }

        if (storedGeometry.geometry != null) {
            return this.serializeGeometry(storedGeometry.geometry);
        }

        return storedGeometry;
    }

    private disposeStoredGeometry(storedGeometry: any) {
        if (storedGeometry != null && storedGeometry.dispose != null) {
            storedGeometry.dispose();
        }
    }

    private normalizeGeometry(source: any, objectId: string): WorkerGeometry.GeometryData {
        try {
            if (source == null) {
                return null;
            }

            if (source.positions != null && source.indices != null) {
                source.id = objectId;
                source.name = source.name || objectId;
                source.colorInfo = source.colorInfo || WorkerGeometry.Utils.cloneColorInfo();

                return source as WorkerGeometry.GeometryData;
            }

            var meshGeometry = WorkerGeometry.Utils.fromMesh(source as BABYLON.Mesh, true);

            if (meshGeometry != null) {
                meshGeometry.id = objectId;
                meshGeometry.name = meshGeometry.name || objectId;
            }

            return meshGeometry;
        }
        catch (error) {
            console.log(error);
            return null;
        }
    }

    private mergeGeometryBatch(masterMeshId: string, meshes: WorkerGeometry.GeometryData[]): WorkerGeometry.GeometryData {

        try {
            var totalPositions = 0;
            var totalNormals = 0;
            var totalIndices = 0;
            var totalUvs = 0;
            var totalColors = 0;
            var hasNormals = false;
            var hasUvs = false;
            var hasColors = false;
            var colorInfo = WorkerGeometry.Utils.cloneColorInfo();

            for (var i = 0; i < meshes.length; i++) {
                totalPositions += meshes[i].positions.length;
                totalIndices += meshes[i].indices.length;
                totalNormals += meshes[i].normals.length;
                totalUvs += meshes[i].uvs.length;
                totalColors += meshes[i].colors.length;
                hasNormals = hasNormals || meshes[i].normals.length > 0;
                hasUvs = hasUvs || meshes[i].uvs.length > 0;
                hasColors = hasColors || meshes[i].colors.length > 0;

                if (meshes[i].colors.length > 0) {
                    colorInfo = WorkerGeometry.Utils.cloneColorInfo(meshes[i].colorInfo);
                }
            }

            var positions = new Float32Array(totalPositions);
            var normals = hasNormals ? new Float32Array(totalNormals) : new Float32Array(0);
            var indices = new Uint32Array(totalIndices);
            var uvs = hasUvs ? new Float32Array(totalUvs) : new Float32Array(0);
            var colors: Float32Array | Uint8Array = hasColors
                ? (colorInfo.componentType === "UNSIGNED_BYTE" ? new Uint8Array(totalColors) : new Float32Array(totalColors))
                : new Float32Array(0);

            var positionOffset = 0;
            var normalOffset = 0;
            var indexOffset = 0;
            var uvOffset = 0;
            var colorOffset = 0;
            var vertexOffset = 0;

            for (var g = 0; g < meshes.length; g++) {
                var mesh = meshes[g];

                positions.set(mesh.positions, positionOffset);

                if (hasNormals && mesh.normals.length > 0) {
                    normals.set(mesh.normals, normalOffset);
                    normalOffset += mesh.normals.length;
                }

                if (hasUvs && mesh.uvs.length > 0) {
                    uvs.set(mesh.uvs, uvOffset);
                    uvOffset += mesh.uvs.length;
                }

                if (hasColors && mesh.colors.length > 0) {
                    (colors as any).set(mesh.colors, colorOffset);
                    colorOffset += mesh.colors.length;
                }

                for (var index = 0; index < mesh.indices.length; index++) {
                    indices[indexOffset + index] = mesh.indices[index] + vertexOffset;
                }

                indexOffset += mesh.indices.length;
                positionOffset += mesh.positions.length;
                vertexOffset += WorkerGeometry.Utils.getVertexCount(mesh);
            }

            return WorkerGeometry.Utils.create(masterMeshId, masterMeshId, positions, indices, normals, uvs, colors, colorInfo);
        }
        catch (error) {
            console.log(error);
            return null;
        }
    }
    clearMeshes() {
        this.clearVertexIndexes();
        this._instancePayload = [];

        for (var i = 0; i < this._masterMeshKeys.length; i++) {
            this.disposeStoredGeometry(this._masterMeshes[this._masterMeshKeys[i]]);
            this._masterMeshes[this._masterMeshKeys[i]] = null;

            if (this._meshesToMerge[this._masterMeshKeys[i]] != null && this._meshesToMerge[this._masterMeshKeys[i]].length > 0) {

                for (var x = 0; x < this._meshesToMerge[this._masterMeshKeys[i]].length; x++) {
                    this._meshesToMerge[this._masterMeshKeys[i]][x] = null;
                }
                this._meshesToMerge[this._masterMeshKeys[i]] = null;
            }
        }

        this._masterMeshToObjectInfo = {};
        this._masterMeshKeys = [];
        this._instancePayload = [];
    }

    addGLBObject(objectId: string, minExtents: BABYLON.Vector3, maxExtents: BABYLON.Vector3, masterMeshId: string, mesh: BABYLON.Mesh) {

        {
            var subMeshId = masterMeshId + "_" + this._meshIdentifier + "_1";

            if (this._mergedMeshKeys.indexOf(subMeshId) != -1) {

                // This base id has already been merged. Therefore, let us append a number on the end of the ID, and 
                // create a new mesh so we do not get meshes that are too large.
                masterMeshId = masterMeshId + "_" + this._meshIdentifier + "_" + this._remappedMeshId[masterMeshId].toString();
            }
            else {
                masterMeshId = subMeshId;
            }

            mesh.id = objectId;

            //var minEx = new BABYLON.Vector3(minExtents.X / 1000, minExtents.Y / 1000, minExtents.Z / 1000);
            //var maxEx = new BABYLON.Vector3(maxExtents.X / 1000, maxExtents.Y / 1000, maxExtents.Z / 1000);
            this.updateMeshExtents(new BABYLON.BoundingInfo(minExtents, maxExtents));

            this.updateMeshObjectInformation(masterMeshId, minExtents, maxExtents, objectId, [mesh]);
            this._masterMeshes[masterMeshId] = mesh;
        }
    }

        addGraphicObjectO(objectId: string, minExtents: Data.GraphicData.Vector3D, maxExtents: Data.GraphicData.Vector3D, masterMeshId: string, meshes: any[]) {


        if (meshes.length > 0) {

            var subMeshId = masterMeshId + "_" + this._meshIdentifier + "_1";

            if (this._mergedMeshKeys.indexOf(subMeshId) != -1) {

                // This base id has already been merged. Therefore, let us append a number on the end of the ID, and 
                // create a new mesh so we do not get meshes that are too large.
                masterMeshId = masterMeshId + "_" + this._meshIdentifier + "_" + this._remappedMeshId[masterMeshId].toString();
            }
            else {
                masterMeshId = subMeshId;
            }

            for (var i = 0; i < meshes.length; i++) {
                var mesh = meshes[i];

                mesh.id = objectId;
                //mesh.material = material;
                masterMeshId = this.addToMeshes(masterMeshId, mesh);
            }

            var minEx = new BABYLON.Vector3(minExtents.X / 1000, minExtents.Y / 1000, minExtents.Z / 1000);
            var maxEx = new BABYLON.Vector3(maxExtents.X / 1000, maxExtents.Y / 1000, maxExtents.Z / 1000);
            this.updateMeshExtents(new BABYLON.BoundingInfo(minEx, maxEx));
            this.updateMeshObjectInformation(masterMeshId, minEx, maxEx, objectId, meshes);

            if (this._meshesToMerge[masterMeshId].length > this.MERGE_MESH_THRESHOLD) {
                this.mergeMeshes(true);
            }
        }
    }

    addGraphicObject(objectId: string, minExtents: Data.GraphicData.Vector3D, maxExtents: Data.GraphicData.Vector3D, masterMeshId: string, meshes: any[]) {

        try {
            if (meshes.length > 0) {
                var geometries: WorkerGeometry.GeometryData[] = [];

                for (var u = 0; u < meshes.length; u++) {
                    var geometry = this.normalizeGeometry(meshes[u], objectId);

                    if (geometry != null) {
                        geometries.push(geometry);
                    }
                }

                if (geometries.length == 0) {
                    return;
                }

                var subMeshId = masterMeshId + "_" + this._meshIdentifier + "_1";

                if (this._mergedMeshKeys.indexOf(subMeshId) != -1) {

                    // This base id has already been merged. Therefore, let us append a number on the end of the ID, and 
                    // create a new mesh so we do not get meshes that are too large.
                    masterMeshId = masterMeshId + "_" + this._meshIdentifier + "_" + this._remappedMeshId[masterMeshId].toString();
                }
                else {
                    masterMeshId = subMeshId;
                }

                // if this object's index count by itself is large, instead just merge these meshes by themselves!
                for (var i = 0; i < geometries.length; i++) {
                    var mesh = geometries[i];

                    if (this._mergeMeshVertex[masterMeshId] == null) {
                        this._mergeMeshVertex[masterMeshId] = 0;
                    }

                    this._mergeMeshVertex[masterMeshId] += WorkerGeometry.Utils.getVertexCount(mesh);
                    this._mergeMeshVertex[masterMeshId] += WorkerGeometry.Utils.getIndexCount(mesh);
                    this._mergeMeshVertex[masterMeshId] += WorkerGeometry.Utils.getUvCount(mesh);

                    mesh.id = objectId;
                    masterMeshId = this.addToMeshes(masterMeshId, mesh);
                }

                var minEx = new BABYLON.Vector3(minExtents.X / 1000, minExtents.Y / 1000, minExtents.Z / 1000);
                var maxEx = new BABYLON.Vector3(maxExtents.X / 1000, maxExtents.Y / 1000, maxExtents.Z / 1000);
                this.updateMeshExtents(new BABYLON.BoundingInfo(minEx, maxEx));

                if (this._mergeMeshesExtentInfo[masterMeshId] == null) {
                    this._mergeMeshesExtentInfo[masterMeshId] = {};
                }

                this._mergeMeshesExtentInfo[masterMeshId][objectId] = new BABYLON.BoundingInfo(minEx, maxEx);

                var vertexCount = this._mergeMeshVertex[masterMeshId];

                if (this._meshesToMerge[masterMeshId].length > this.MERGE_MESH_THRESHOLD || vertexCount > this.MESH_VERTES_SPLIT_THRESHOLD) {
                    this.numMerges++;

                    this.mergeSingleMesh(masterMeshId);
                }
            }
        }
        catch (error) {
            console.log(error);
        }
    }

    addCylinderInstances(objectId: string, minExtents: Data.GraphicData.Vector3D, maxExtents: Data.GraphicData.Vector3D, materialId: string, instances: Data.GraphicData.CylinderInstanceTransform[]): void {

        if (!instances || instances.length === 0) {
            return;
        }

        this._instancePayload.push({
            ObjectId: objectId,
            MaterialKey: materialId,
            MinExtents: minExtents,
            MaxExtents: maxExtents,
            Instances: instances
        });

        var minEx = new BABYLON.Vector3(minExtents.X / 1000, minExtents.Y / 1000, minExtents.Z / 1000);
        var maxEx = new BABYLON.Vector3(maxExtents.X / 1000, maxExtents.Y / 1000, maxExtents.Z / 1000);
        this.updateMeshExtents(new BABYLON.BoundingInfo(minEx, maxEx));
    }

    getRepositoryInstanceData(): Data.GraphicData.AssetInstancePayload[] {
        return this._instancePayload;
    }

    mergeSingleMesh(masterMeshId: string) {
        try {
            // Materials are the "keys" in the meshes to merge, so for all the materials that have been added, merge on all the waiting meshes.       
            if (this._meshesToMerge[masterMeshId]) {

                if (this._meshesToMerge[masterMeshId].length > 0) {

                    if (this._masterMeshes[masterMeshId] != null) {
                        this._meshesToMerge[masterMeshId].unshift(WorkerGeometry.Utils.clone(this._masterMeshes[masterMeshId]));
                    }

                    var totalVerts = 0;
                    var startIndexToMerge = 0;

                    var mergeLength = this._meshesToMerge[masterMeshId].length;

                    var origLength = this._meshesToMerge[masterMeshId].length;
                    var numSplits = 0;
                    var numsliced = 0;
                    var hasColor = null;
                    var forceSplit = false;
                    var hasNormals = null;
                    var colorSignature: string = null;

                    for (var indexToMerge = 0; (indexToMerge < mergeLength) && mergeLength > 0; indexToMerge++) {

                        var nextVertCount = 0;

                        {
                            nextVertCount += WorkerGeometry.Utils.getVertexCount(this._meshesToMerge[masterMeshId][indexToMerge]);

                            var currentHasColor = WorkerGeometry.Utils.hasColors(this._meshesToMerge[masterMeshId][indexToMerge]);
                            var currentHasNormals = WorkerGeometry.Utils.hasNormals(this._meshesToMerge[masterMeshId][indexToMerge]);
                            var currentColorSignature = WorkerGeometry.Utils.getColorSignature(this._meshesToMerge[masterMeshId][indexToMerge]);

                            if (hasColor == null)
                                hasColor = currentHasColor;

                            if (hasNormals == null)
                                hasNormals = currentHasNormals;

                            if (colorSignature == null)
                                colorSignature = currentColorSignature;

                            if (((totalVerts + nextVertCount > this.MESH_VERTES_SPLIT_THRESHOLD))) {
                                forceSplit = true;
                            }
                        }

                        if (indexToMerge + 1 < mergeLength) {
                            // If the next mesh is different colorization, force this merge
                            var nextHasColor = WorkerGeometry.Utils.hasColors(this._meshesToMerge[masterMeshId][indexToMerge + 1]);
                            var nextHasNormals = WorkerGeometry.Utils.hasNormals(this._meshesToMerge[masterMeshId][indexToMerge + 1]);
                            var nextColorSignature = WorkerGeometry.Utils.getColorSignature(this._meshesToMerge[masterMeshId][indexToMerge + 1]);

                            if ((nextHasColor != hasColor))
                                forceSplit = true;

                            if ((nextHasNormals != hasNormals))
                                forceSplit = true;

                            if ((nextColorSignature != colorSignature))
                                forceSplit = true;
                        }

                        if ((indexToMerge + 1) == mergeLength || totalVerts > this.MESH_VERTES_SPLIT_THRESHOLD || forceSplit || indexToMerge > 500) {

                            numSplits++;

                            var meshesToMerge = this._meshesToMerge[masterMeshId].splice(0, indexToMerge + 1);
                            numsliced += meshesToMerge.length;

                            this._masterMeshToObjectInfo[masterMeshId + "_" + numSplits] = this.combineMeshObjectInformation(masterMeshId, masterMeshId + "_" + numSplits, meshesToMerge);

                            var mergedMesh = this.mergeGeometryBatch(masterMeshId + "_" + numSplits, meshesToMerge);

                            this._masterMeshes[masterMeshId + "_" + numSplits] = mergedMesh;
                            this._masterMeshKeys.push(masterMeshId + "_" + numSplits);

                            totalVerts = 0;
                            forceSplit = false;
                            mergeLength -= indexToMerge + 1;
                            indexToMerge = -1;
                            hasColor = null;
                            hasNormals = null;
                            colorSignature = null;
                        }
                        else {

                            totalVerts += nextVertCount;
                        }
                    }

                    this._mergedMeshKeys.push(masterMeshId);

                    var mergeMeshKey = masterMeshId;

                    var keyParts = mergeMeshKey.split("_");
                    var baseKey = keyParts[0];

                    var subMeshId = Number(keyParts[2]);

                    this._remappedMeshId[baseKey] = subMeshId + 1;

                    this._meshesToMerge[masterMeshId] = [];
                    this._masterMeshKeys.splice(this._masterMeshKeys.indexOf(masterMeshId), 1);
                    this._mergeMeshVertex[masterMeshId] = 0;
                }
            }
        }
        finally {

            this._mergeMeshesExtentInfo[masterMeshId] = {};
        }
    }

    // This is a custom implementation of the transformation of vertex data since babylon 3.3 has a serious bug and flips its indicies, resulting
    // in normals that are all messed up!
    public transformVertexData(data: BABYLON.VertexData, matrix: BABYLON.Matrix): BABYLON.VertexData {
        var transformed = BABYLON.Vector3.Zero();
        var index: number;
        if (data.positions) {
            var position = BABYLON.Vector3.Zero();

            for (index = 0; index < data.positions.length; index += 3) {
                BABYLON.Vector3.FromArrayToRef(data.positions, index, position);

                BABYLON.Vector3.TransformCoordinatesToRef(position, matrix, transformed);

                data.positions[index] = transformed.x;
                data.positions[index + 1] = transformed.y;
                data.positions[index + 2] = transformed.z;
            }
        }

        if (data.normals) {
            var normal = BABYLON.Vector3.Zero();

            for (index = 0; index < data.normals.length; index += 3) {
                BABYLON.Vector3.FromArrayToRef(data.normals, index, normal);

                BABYLON.Vector3.TransformNormalToRef(normal, matrix, transformed);
                data.normals[index] = transformed.x;
                data.normals[index + 1] = transformed.y;
                data.normals[index + 2] = transformed.z;
            }
        }

        if (data.tangents) {
            var tangent = BABYLON.Vector4.Zero();
            var tangentTransformed = BABYLON.Vector4.Zero();

            for (index = 0; index < data.tangents.length; index += 4) {
                BABYLON.Vector4.FromArrayToRef(data.tangents, index, tangent);

                BABYLON.Vector4.TransformNormalToRef(tangent, matrix, tangentTransformed);
                data.tangents[index] = tangentTransformed.x;
                data.tangents[index + 1] = tangentTransformed.y;
                data.tangents[index + 2] = tangentTransformed.z;
                data.tangents[index + 3] = tangentTransformed.w;
            }
        }

        return data;
    }

    // Override of babylon merge mesh, so that transform vertex data is called, instead of babylon's which causes normal errors.
    public MergeMeshesInternal(meshes: Array<BABYLON.Mesh>, disposeSource = true, allow32BitsIndices?: boolean, meshSubclass?: BABYLON.Mesh, subdivideWithSubMeshes?: boolean): BABYLON.Mesh {
        var index: number;
        if (!allow32BitsIndices) {
            var totalVertices = 0;

            // Counting vertices
            for (index = 0; index < meshes.length; index++) {
                if (meshes[index]) {
                    totalVertices += meshes[index].getTotalVertices();

                    if (totalVertices > 65536) {
                        BABYLON.Tools.Warn("Cannot merge meshes because resulting mesh will have more than 65536 vertices. Please use allow32BitsIndices = true to use 32 bits indices");
                        return null;
                    }
                }
            }
        }

        // Merge
        var vertexData: BABYLON.VertexData;
        var otherVertexData: BABYLON.VertexData;
        var indiceArray: Array<number> = new Array<number>();
        var source: BABYLON.Mesh;

        for (index = 0; index < meshes.length; index++) {

            if (meshes[index]) {

                meshes[index].computeWorldMatrix(true);

                otherVertexData = this.ExtractFromMeshInternal(meshes[index], true);

                this.transformVertexData(otherVertexData, meshes[index].getWorldMatrix());

                if (vertexData) {
                    vertexData.merge(otherVertexData, allow32BitsIndices);
                } else {
                    vertexData = otherVertexData;
                    source = meshes[index];
                }

                if (subdivideWithSubMeshes) {
                    indiceArray.push(meshes[index].getTotalIndices());
                }
            }
        }

        if (!meshSubclass) {
            meshSubclass = new BABYLON.Mesh(source.name + "_merged", source.getScene());
        }
        vertexData.applyToMesh(meshSubclass);

        // Setting properties
        meshSubclass.material = source.material;
        meshSubclass.checkCollisions = source.checkCollisions;

        // Cleaning
        if (disposeSource) {
            for (index = 0; index < meshes.length; index++) {
                if (meshes[index]) {
                    meshes[index].dispose();
                }
            }
        }

        // Subdivide
        if (subdivideWithSubMeshes) {

            //-- Suppresions du submesh global
            meshSubclass.releaseSubMeshes();
            index = 0;
            var offset = 0;

            //-- aplique la subdivision en fonction du tableau d'indices
            while (index < indiceArray.length) {
                BABYLON.SubMesh.CreateFromIndices(0, offset, indiceArray[index], meshSubclass);
                offset += indiceArray[index];
                index++;
            }
        }

        return meshSubclass;
    }

    // CUstom override of mesh extraciton that exludes the uvs. This is done because merge mesh does not work when merging
    // some meshes WITH uvs and some without. Therefore, we always do not extract the uvs from any meshes to be merged.
    private ExtractFromMeshInternal(meshOrGeometry: BABYLON.IGetSetVerticesData, copyWhenShared?: boolean, forceCopy?: boolean): BABYLON.VertexData {
        var result = new BABYLON.VertexData();

        if (meshOrGeometry.isVerticesDataPresent(BABYLON.VertexBuffer.PositionKind)) {
            result.positions = meshOrGeometry.getVerticesData(BABYLON.VertexBuffer.PositionKind, copyWhenShared, forceCopy);
        }

        if (meshOrGeometry.isVerticesDataPresent(BABYLON.VertexBuffer.NormalKind)) {
            result.normals = meshOrGeometry.getVerticesData(BABYLON.VertexBuffer.NormalKind, copyWhenShared, forceCopy);
        }

        if (meshOrGeometry.isVerticesDataPresent(BABYLON.VertexBuffer.TangentKind)) {
            result.tangents = meshOrGeometry.getVerticesData(BABYLON.VertexBuffer.TangentKind, copyWhenShared, forceCopy);
        }

        if (meshOrGeometry.isVerticesDataPresent(BABYLON.VertexBuffer.ColorKind)) {
            result.colors = meshOrGeometry.getVerticesData(BABYLON.VertexBuffer.ColorKind, copyWhenShared, forceCopy);
        }

        if (meshOrGeometry.isVerticesDataPresent(BABYLON.VertexBuffer.MatricesIndicesKind)) {
            result.matricesIndices = meshOrGeometry.getVerticesData(BABYLON.VertexBuffer.MatricesIndicesKind, copyWhenShared, forceCopy);
        }

        if (meshOrGeometry.isVerticesDataPresent(BABYLON.VertexBuffer.MatricesWeightsKind)) {
            result.matricesWeights = meshOrGeometry.getVerticesData(BABYLON.VertexBuffer.MatricesWeightsKind, copyWhenShared, forceCopy);
        }

        if (meshOrGeometry.isVerticesDataPresent(BABYLON.VertexBuffer.MatricesIndicesExtraKind)) {
            result.matricesIndicesExtra = meshOrGeometry.getVerticesData(BABYLON.VertexBuffer.MatricesIndicesExtraKind, copyWhenShared, forceCopy);
        }

        if (meshOrGeometry.isVerticesDataPresent(BABYLON.VertexBuffer.MatricesWeightsExtraKind)) {
            result.matricesWeightsExtra = meshOrGeometry.getVerticesData(BABYLON.VertexBuffer.MatricesWeightsExtraKind, copyWhenShared, forceCopy);
        }

        result.indices = meshOrGeometry.getIndices(copyWhenShared, forceCopy);

        return result;
    }

    mergeMeshes(forceMerge: boolean) {

        try {

            var keysMerged: any[] = [];
            var keysToMerge: any[] = [];

            if (forceMerge) {
                // Materials are the "keys" in the meshes to merge, so for all the materials that have been added, merge on all the waiting meshes.       
                for (var i = 0; i < this._masterMeshKeys.length; i++) {

                    if (this._meshesToMerge[this._masterMeshKeys[i]]) {

                        if ((forceMerge && this._meshesToMerge[this._masterMeshKeys[i]].length > 0) || this._meshesToMerge[this._masterMeshKeys[i]].length > this.MERGE_MESH_THRESHOLD) {

                            if (this._meshesToMerge[this._masterMeshKeys[i]].length > 0) {

                                keysToMerge.push(this._masterMeshKeys[i]);
                            }
                        }
                    }
                }
            }

            for (var z = 0; z < keysToMerge.length; z++) {

                this.mergeSingleMesh(keysToMerge[z]);
            }

        }
        catch (error) {
            console.log(error);
        }
    }
    private combineMeshObjectInformation(mergeKey: string, masterMeshId: string, meshes: WorkerGeometry.GeometryData[]): SubMeshObjectInfo[] {

        // if a mesh has the same id, it is part of the same object. We will therefore crunch the meshes into a single info.

        var totalVerts = 0;
        var totalInd = 0;
        var totalUvs = 0;

        var vertexArray: SubMeshObjectInfo[] = [];

        var tailIndices = vertexArray.length > 0 ? vertexArray[vertexArray.length - 1].indiciesStartIndex + vertexArray[vertexArray.length - 1].indicesCount : 0;
        var tailVertexIndex = vertexArray.length > 0 ? vertexArray[vertexArray.length - 1].vertexStartIndex + vertexArray[vertexArray.length - 1].vertexCount : 0;
        var tailUvIndex = vertexArray.length > 0 ? vertexArray[vertexArray.length - 1].uvStartIndex + vertexArray[vertexArray.length - 1].uvCount : 0;

        var insertIndices = 0;
        var insertVertexIndex = 0;
        var insertUvIndex = 0;

        for (var i = 0; i < meshes.length; i++) {

            insertIndices += WorkerGeometry.Utils.getIndexCount(meshes[i]);
            insertVertexIndex += WorkerGeometry.Utils.getVertexCount(meshes[i]);
            insertUvIndex += WorkerGeometry.Utils.getUvCount(meshes[i]);


            if ((i + 1) != meshes.length && meshes[i].id == meshes[i + 1].id) {

            }
            else {

                var minExtents = this._mergeMeshesExtentInfo[mergeKey][meshes[i].id].minimum;
                var maxExtents = this._mergeMeshesExtentInfo[mergeKey][meshes[i].id].maximum;

                var faceInfo = new SubMeshObjectInfo();

                faceInfo.objectId = meshes[i].id;
                faceInfo.indiciesStartIndex = tailIndices;
                faceInfo.vertexStartIndex = tailVertexIndex;
                faceInfo.uvStartIndex = tailUvIndex;
                faceInfo.indicesCount = insertIndices;
                faceInfo.vertexCount = insertVertexIndex;
                faceInfo.uvCount = insertUvIndex;
                faceInfo.minExtents = minExtents;
                faceInfo.maxExtents = maxExtents;
                faceInfo.materialOverride = "";

                vertexArray.push(faceInfo);

                tailIndices += insertIndices;
                tailVertexIndex += insertVertexIndex;
                tailUvIndex += insertUvIndex;

                insertIndices = 0;
                insertVertexIndex = 0;
                insertUvIndex = 0;
            }
        }

        return vertexArray;
    }

    private updateMeshObjectInformation(masterMeshId: string, minExtents: BABYLON.Vector3, maxExtents: BABYLON.Vector3, objectId: string, meshes: BABYLON.Mesh[]) {

        var totalVerts = 0;
        var totalInd = 0;
        var totalUvs = 0;

        var vertexArray = this._masterMeshToObjectInfo[masterMeshId];
        if (vertexArray == null) {
            vertexArray = [];
            this._masterMeshToObjectInfo[masterMeshId] = vertexArray;
        }

        if (this._masterMeshKeys.indexOf(masterMeshId) == -1) {
            this._masterMeshKeys.push(masterMeshId);
        }

        var tailIndices = vertexArray.length > 0 ? vertexArray[vertexArray.length - 1].indiciesStartIndex + vertexArray[vertexArray.length - 1].indicesCount : 0;
        var tailVertexIndex = vertexArray.length > 0 ? vertexArray[vertexArray.length - 1].vertexStartIndex + vertexArray[vertexArray.length - 1].vertexCount : 0;
        var tailUvIndex = vertexArray.length > 0 ? vertexArray[vertexArray.length - 1].uvStartIndex + vertexArray[vertexArray.length - 1].uvCount : 0;

        var insertIndices = 0;
        var insertVertexIndex = 0;
        var insertUvIndex = 0;

        for (var i = 0; i < meshes.length; i++) {
            var totalFaces = meshes[i].getTotalIndices() / 3;

            var uvBuffer = meshes[i].getVertexBuffer(BABYLON.VertexBuffer.UVKind);
            var uvs = uvBuffer ? meshes[i].getVertexBuffer(BABYLON.VertexBuffer.UVKind).getData() : null;

            insertIndices += meshes[i].getTotalIndices();
            insertVertexIndex += meshes[i].getTotalVertices();
            insertUvIndex += uvs ? meshes[i].getVertexBuffer(BABYLON.VertexBuffer.UVKind).getSize() : 0;
        }

        var faceInfo = new SubMeshObjectInfo();

        faceInfo.objectId = objectId;
        faceInfo.indiciesStartIndex = tailIndices;
        faceInfo.vertexStartIndex = tailVertexIndex;
        faceInfo.uvStartIndex = tailUvIndex;
        faceInfo.indicesCount = insertIndices;
        faceInfo.vertexCount = insertVertexIndex;
        faceInfo.uvCount = insertUvIndex;
        faceInfo.minExtents = minExtents;
        faceInfo.maxExtents = maxExtents;
        faceInfo.materialOverride = "";

        vertexArray.push(faceInfo);
    }

    private clearVertexIndexes() {
        for (var i = 0; i < this._masterMeshKeys.length; i++) {
            this._masterMeshToObjectInfo[this._masterMeshKeys[i]] = [];
        }

        this._masterMeshKeys = [];
    }

    private addToMeshes(meshId: string, mesh: WorkerGeometry.GeometryData): string {

        var meshArray = this._meshesToMerge[meshId];
        if (meshArray == null) {
            meshArray = [];
            this._meshesToMerge[meshId] = meshArray;
        }


        if (this._masterMeshKeys.indexOf(meshId) == -1) {
            this._masterMeshKeys.push(meshId);
        }

        meshArray.push(mesh);

        return meshId;
    }

    private updateMeshExtents(boundingInfo: BABYLON.BoundingInfo) {
        if (this._sceneContentExtents == null)
            this._sceneContentExtents = new BABYLON.BoundingBox(new BABYLON.Vector3(boundingInfo.minimum.x - 1, boundingInfo.minimum.y - 1, boundingInfo.minimum.z - 1), new BABYLON.Vector3(boundingInfo.maximum.x + 1, boundingInfo.maximum.y + 1, boundingInfo.maximum.z + 1));
        else {

            if (this._sceneContentExtents.minimum.x > boundingInfo.minimum.x) {
                this._sceneContentExtents.minimum.x = boundingInfo.minimum.x;
            }
            if (this._sceneContentExtents.minimum.y > boundingInfo.minimum.y) {
                this._sceneContentExtents.minimum.y = boundingInfo.minimum.y;
            }
            if (this._sceneContentExtents.minimum.z > boundingInfo.minimum.z) {
                this._sceneContentExtents.minimum.z = boundingInfo.minimum.z;
            }

            if (this._sceneContentExtents.maximum.x < boundingInfo.maximum.x) {
                this._sceneContentExtents.maximum.x = boundingInfo.maximum.x;
            }
            if (this._sceneContentExtents.maximum.y < boundingInfo.maximum.y) {
                this._sceneContentExtents.maximum.y = boundingInfo.maximum.y;
            }
            if (this._sceneContentExtents.maximum.z < boundingInfo.maximum.z) {
                this._sceneContentExtents.maximum.z = boundingInfo.maximum.z;
            }
        }
    }
}

// Class used for to keep track of object info with respect to the master meshes.
class SubMeshObjectInfo {
    public vertexStartIndex: number;
    public vertexCount: number;
    public indiciesStartIndex: number;
    public indicesCount: number;
    public uvStartIndex: number;
    public uvCount: number;
    public objectId: string;
    public minExtents: BABYLON.Vector3;
    public maxExtents: BABYLON.Vector3;
    public materialOverride: string;
}

export default MeshRepository;
